<template>
  <div class="container-fluid mt-3">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2>Transaction Performance vs Market</h2>
        <p class="text-muted">
          Compare Soldera's sales transaction performance against market
          benchmarks (GO - Nordic Hydro)
        </p>
      </div>
      <div class="d-flex gap-2">
        <button
          class="btn btn-primary"
          @click="exportToCsv"
          :disabled="isLoading || filteredTransactions.length === 0"
        >
          <font-awesome-icon icon="download" /> Export Transactions
        </button>
        <button
          class="btn btn-outline-primary"
          @click="exportMarketPricesToCsv"
          :disabled="isLoading || marketPrices.length === 0"
        >
          <font-awesome-icon icon="download" /> Export Market Prices
        </button>
      </div>
    </div>

    <!-- Date Range and Vintage Filter -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row align-items-end">
          <div class="col-md-3">
            <label for="vintage" class="form-label">Vintage (Year)</label>
            <input
              id="vintage"
              v-model.number="vintage"
              type="number"
              class="form-control"
              @change="handleFilterChange"
            />
          </div>
          <div class="col-md-3">
            <label for="dateFrom" class="form-label">Date From</label>
            <input
              id="dateFrom"
              v-model="dateFrom"
              type="date"
              class="form-control"
              @change="handleFilterChange"
            />
          </div>
          <div class="col-md-3">
            <label for="dateTo" class="form-label">Date To</label>
            <input
              id="dateTo"
              v-model="dateTo"
              type="date"
              class="form-control"
              @change="handleFilterChange"
            />
          </div>
          <div class="col-md-3">
            <button class="btn btn-secondary w-100" @click="setDefaultFilters">
              Reset to Last 12 Months
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <LoadingIndicatorComponent v-if="isLoading" />

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <!-- Content -->
    <div v-else>
      <!-- Performance Stats -->
      <PerformanceStats :metrics="computedMetrics" />

      <!-- Chart -->
      <div class="card mb-4">
        <div class="card-header">
          <h5 class="mb-0">Performance Chart</h5>
          <small class="text-muted"> Showing included transactions only </small>
        </div>
        <div class="card-body">
          <PerformanceChart
            :market-prices="marketPrices"
            :transactions="filteredTransactions"
          />
        </div>
      </div>

      <!-- Transactions Table -->
      <div class="card mb-4">
        <div
          class="card-header d-flex justify-content-between align-items-center"
        >
          <div>
            <h5 class="mb-0">Transactions</h5>
            <small class="text-muted">
              Uncheck transactions to exclude them from analysis
            </small>
          </div>
          <div>
            <button
              class="btn btn-sm btn-outline-secondary me-2"
              @click="toggleForwardTransactions"
            >
              {{
                areForwardTransactionsHidden
                  ? "Show All Forwards"
                  : "Hide All Forwards"
              }}
            </button>
          </div>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-striped table-hover mb-0">
              <thead>
                <tr>
                  <th style="width: 40px" class="text-center">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      :checked="areAllIncluded"
                      @change="toggleAllTransactions"
                    />
                  </th>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Client</th>
                  <th>Description</th>
                  <th class="text-end">Volume</th>
                  <th class="text-end">Unit Price</th>
                  <th class="text-end">Market Ref</th>
                  <th class="text-end">Delta</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="t in allTransactions" :key="t.id">
                  <td class="text-center">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      :checked="!excludedTransactionIds.has(t.id)"
                      @change="toggleTransactionExclusion(t.id)"
                    />
                  </td>
                  <td>{{ t.id }}</td>
                  <td>
                    <span
                      v-if="t.is_forward"
                      class="badge bg-warning text-dark"
                    >
                      Forward
                    </span>
                    <span v-else class="badge bg-secondary">Spot</span>
                  </td>
                  <td>{{ new Date(t.transaction_at).toLocaleDateString() }}</td>
                  <td>{{ t.counterparty_name || "-" }}</td>
                  <td>{{ t.description || "-" }}</td>
                  <td class="text-end">{{ t.amount }} MWh</td>
                  <td class="text-end">
                    €{{ t.unit_price }}
                    <div><EurConversionBadge :conversion="t" /></div>
                  </td>
                  <td class="text-end">
                    {{
                      t.reference_market_price
                        ? "€" + parseFloat(t.reference_market_price).toFixed(2)
                        : "-"
                    }}
                  </td>
                  <td class="text-end">
                    <span :class="getDeltaClass(t.delta_vs_market)">
                      {{
                        t.delta_vs_market
                          ? (parseFloat(t.delta_vs_market) > 0 ? "+" : "") +
                            parseFloat(t.delta_vs_market).toFixed(2)
                          : "-"
                      }}
                    </span>
                  </td>
                </tr>
                <tr v-if="allTransactions.length === 0">
                  <td colspan="10" class="text-center py-3 text-muted">
                    No transactions found for this vintage.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import EurConversionBadge from "@/money/EurConversionBadge.vue";
import { downloadFromBlob } from "@/utils/downloadUtils";
import { computed, onMounted, ref } from "vue";
import PerformanceChart from "./PerformanceChart.vue";
import PerformanceStats from "./PerformanceStats.vue";
import {
  fetchTransactionPerformance,
  type MarketPriceData,
  type PerformanceMetrics,
  type TransactionPerformanceData,
} from "./transactionPerformanceApi";

const { globalErrorHandler } = useGlobalHandlers();

// State
const isLoading = ref(false);
const error = ref<string | null>(null);
const marketPrices = ref<MarketPriceData[]>([]);
const allTransactions = ref<TransactionPerformanceData[]>([]);
const excludedTransactionIds = ref<Set<number>>(new Set());

// Filters
const vintage = ref<number>(new Date().getFullYear());
const dateFrom = ref<string>("");
const dateTo = ref<string>("");

// Set default filters (last 12 months, current year)
const setDefaultFilters = () => {
  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(today.getFullYear() - 1);

  vintage.value = today.getFullYear();
  dateTo.value = today.toISOString().split("T")[0];
  dateFrom.value = oneYearAgo.toISOString().split("T")[0];

  fetchData();
};

// Filtered transactions (excluding excluded ones)
const filteredTransactions = computed(() => {
  return allTransactions.value.filter(
    (t) => !excludedTransactionIds.value.has(t.id)
  );
});

const areAllIncluded = computed(() => {
  return (
    allTransactions.value.length > 0 && excludedTransactionIds.value.size === 0
  );
});

const areForwardTransactionsHidden = computed(() => {
  const forwardTransactions = allTransactions.value.filter((t) => t.is_forward);
  if (forwardTransactions.length === 0) return false;
  return forwardTransactions.every((t) =>
    excludedTransactionIds.value.has(t.id)
  );
});

// Computed metrics based on filtered transactions
const computedMetrics = computed<PerformanceMetrics>(() => {
  const transactions = filteredTransactions.value;
  const total = transactions.length;
  const withMarketPrice = transactions.filter(
    (t) => t.reference_market_price !== null
  ).length;

  let avgUnitPrice: string | null = null;
  let avgMarketPrice: string | null = null;
  let avgPremium: string | null = null;
  let weightedAvgPremium: string | null = null;
  let weightedAvgMarketPrice: string | null = null;

  if (total > 0) {
    const totalUnitPrice = transactions.reduce(
      (sum, t) => sum + parseFloat(t.unit_price),
      0
    );
    avgUnitPrice = (totalUnitPrice / total).toFixed(4);
  }

  if (withMarketPrice > 0) {
    const withPrice = transactions.filter(
      (t) => t.reference_market_price !== null
    );
    const totalMarketPrice = withPrice.reduce(
      (sum, t) => sum + parseFloat(t.reference_market_price!),
      0
    );
    avgMarketPrice = (totalMarketPrice / withMarketPrice).toFixed(4);

    const totalPremium = withPrice.reduce(
      (sum, t) => sum + (t.delta_vs_market ? parseFloat(t.delta_vs_market) : 0),
      0
    );
    avgPremium = (totalPremium / withMarketPrice).toFixed(4);

    const totalVolume = withPrice.reduce((sum, t) => sum + t.amount, 0);
    if (totalVolume > 0) {
      const totalWeightedPremium = withPrice.reduce(
        (sum, t) =>
          sum +
          (t.delta_vs_market ? parseFloat(t.delta_vs_market) : 0) * t.amount,
        0
      );
      weightedAvgPremium = (totalWeightedPremium / totalVolume).toFixed(4);

      const totalWeightedMarketPrice = withPrice.reduce(
        (sum, t) =>
          sum +
          (t.reference_market_price
            ? parseFloat(t.reference_market_price)
            : 0) *
            t.amount,
        0
      );
      weightedAvgMarketPrice = (totalWeightedMarketPrice / totalVolume).toFixed(
        4
      );
    }
  }

  return {
    total_transactions: total,
    transactions_with_market_price: withMarketPrice,
    average_unit_price: avgUnitPrice,
    average_market_price: avgMarketPrice,
    average_premium: avgPremium,
    weighted_average_premium: weightedAvgPremium,
    weighted_average_market_price: weightedAvgMarketPrice,
  };
});

// Fetch data from API
const fetchData = async () => {
  if (!vintage.value) return;

  isLoading.value = true;
  error.value = null;

  try {
    const response = await fetchTransactionPerformance(
      vintage.value,
      dateFrom.value || undefined,
      dateTo.value || undefined
    );

    marketPrices.value = response.market_prices;
    allTransactions.value = response.transactions;
    // Reset exclusions when fetching new data
    excludedTransactionIds.value.clear();
  } catch (err: unknown) {
    globalErrorHandler(err);
    error.value = err instanceof Error ? err.message : "Failed to fetch data";
  } finally {
    isLoading.value = false;
  }
};

// Handle filter change
const handleFilterChange = () => {
  if (vintage.value && dateFrom.value && dateTo.value) {
    fetchData();
  }
};

// Toggle transaction exclusion
const toggleTransactionExclusion = (transactionId: number) => {
  if (excludedTransactionIds.value.has(transactionId)) {
    excludedTransactionIds.value.delete(transactionId);
  } else {
    excludedTransactionIds.value.add(transactionId);
  }
};

const toggleAllTransactions = () => {
  if (areAllIncluded.value) {
    // Exclude all
    allTransactions.value.forEach((t) =>
      excludedTransactionIds.value.add(t.id)
    );
  } else {
    // Include all
    excludedTransactionIds.value.clear();
  }
};

const toggleForwardTransactions = () => {
  const forwardTransactions = allTransactions.value.filter((t) => t.is_forward);
  if (forwardTransactions.length === 0) return;

  if (areForwardTransactionsHidden.value) {
    // Show all forwards
    forwardTransactions.forEach((t) =>
      excludedTransactionIds.value.delete(t.id)
    );
  } else {
    // Hide all forwards
    forwardTransactions.forEach((t) => excludedTransactionIds.value.add(t.id));
  }
};

const getDeltaClass = (delta: string | null) => {
  if (!delta) return "";
  const val = parseFloat(delta);
  return val > 0 ? "text-success" : val < 0 ? "text-danger" : "";
};

// The rate a non-EUR sale's EUR figures were converted at, marked when it is not yet the deal's own.
const eurRateCell = (t: TransactionPerformanceData): string =>
  t.eur_rate == null
    ? "N/A"
    : `${t.eur_rate}${t.eur_rate_indicative ? " (indicative)" : ""}`;

const downloadCsv = (
  headers: string[],
  rows: (string | number)[][],
  filename: string
) => {
  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${cell}"`).join(","))
    .join("\n");

  downloadFromBlob(csvContent, filename, "text/csv;charset=utf-8;");
};

// Export transactions to CSV
const exportToCsv = () => {
  const transactions = filteredTransactions.value;

  if (transactions.length === 0) {
    return;
  }

  const headers = [
    "Transaction ID",
    "Type",
    "Date",
    "Client",
    "Description",
    "Volume (MWh)",
    "Unit Price (€/MWh)",
    "Market Price (€/MWh)",
    "Delta vs Market (€/MWh)",
    "Currency",
    "EUR rate",
  ];

  const rows = transactions.map((t) => {
    const date = new Date(t.transaction_at).toISOString().split("T")[0];
    return [
      t.id.toString(),
      t.is_forward ? "Forward" : "Spot",
      date,
      t.counterparty_name || "N/A",
      t.description || "N/A",
      t.amount.toString(),
      t.unit_price,
      t.reference_market_price || "N/A",
      t.delta_vs_market || "N/A",
      t.currency,
      eurRateCell(t),
    ];
  });

  const filename = `transaction-performance-v${vintage.value}-${dateFrom.value}-${dateTo.value}.csv`;
  downloadCsv(headers, rows, filename);
};

// Export market prices to CSV
const exportMarketPricesToCsv = () => {
  const prices = marketPrices.value;

  if (prices.length === 0) {
    return;
  }

  const headers = ["Date", "Market Bid (€/MWh)"];

  const rows = prices.map((price) => {
    const date = price.date.split("T")[0];
    const bid = price.bid !== null ? parseFloat(price.bid).toFixed(4) : "";
    return [date, bid];
  });

  const filename = `market-prices-v${vintage.value}-${dateFrom.value}-${dateTo.value}.csv`;
  downloadCsv(headers, rows, filename);
};

// Initialize on mount
onMounted(() => {
  setDefaultFilters();
});
</script>

<style scoped>
.card {
  margin-bottom: 1rem;
}
</style>
